import React, { useEffect, useMemo, useState } from "react"

import EventEmitter from "src/EventEmitter"

import { gql, useClient, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import AutocompleteSelect from "components/Forms/AutocompleteSelect"
import NotificationContainer from "components/NotificationContainer"
import Pagination from "components/Pagination"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import List from "./List"
import NewThread from "./NewThread"

export type MessageThreadsIndexProps = {
  userId: string
  userType: "Student" | "Tutor" | "Parent"
}

const MessageThreadsIndex: React.FC<MessageThreadsIndexProps> = ({
  userId,
  userType,
}) => {
  const client = useClient()
  const [page, setPage] = useState(1)
  const [userFilter, setUserFilter] = useState<
    undefined | Record<string, string>
  >()
  const [result, refreshQuery] = useQuery({
    query: threadsQuery,
    variables: { page, userIdFilter: userFilter?.value },
    client,
  })

  useEffect(() => {
    setPage(1)
  }, [userFilter])

  const channelProps = useMemo(() => {
    return { id: userId }
  }, [userId])

  useWebsocket({
    channelName: "MessageUserChannel",
    channelProps,
  })

  useEffect(() => {
    const handleEvent = () => {
      refreshQuery({ requestPolicy: "network-only" })
    }
    EventEmitter.subscribe("messageReceived", handleEvent)

    return () => {
      EventEmitter.unsubscribe("messageReceived", handleEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <NotificationContainer />
      <h1>Messages</h1>
      <NewThread userType={userType} />

      <UrqlLoadingIndicator result={result}>
        {({ data: { viewer } }) => (
          <>
            {(userFilter ||
              viewer.messageThreads.data.length > 3 ||
              viewer.messageThreads.totalPages > 1) && (
              <div className="mt-6 space-y-4 sm:space-y-0">
                <div className="mr-6 inline-block w-[250px]">
                  <AutocompleteSelect
                    api={`/api/${userType.toLowerCase()}s/message_threads/autocomplete_messageable_users`}
                    onChange={selected => setUserFilter(selected)}
                    isClearable
                    placeholder="Filter Threads by User"
                    value={userFilter}
                  />
                </div>
              </div>
            )}

            <List threads={viewer.messageThreads.data} viewerId={viewer.id} />
            <Pagination
              currentPage={viewer.messageThreads.currentPage}
              totalPages={viewer.messageThreads.totalPages}
              changePage={setPage}
            />
          </>
        )}
      </UrqlLoadingIndicator>
    </>
  )
}

const threadsQuery = gql`
  query ThreadsQuery($page: Int!, $userIdFilter: ID) {
    viewer {
      id
      messageThreads(page: $page, userIdFilter: $userIdFilter) {
        currentPage
        totalPages
        data {
          id
          title
          showPath
          messagesCount
          users {
            id
            type
            messagingDisplayName
            avatar {
              thumbUrl
            }
          }
          lastMessage {
            id
            read
            content
            createdAt
            author {
              id
              type
              messagingDisplayName
              showPath
              avatar {
                thumbUrl
              }
            }
          }
        }
      }
    }
  }
`

export default MessageThreadsIndex
